import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import {
  adminAuth,
  adminDownloadCsv,
  adminFetch,
  adminLogin,
  formatApiError,
} from "@/lib/adminApi";
import {
  LogOut,
  RefreshCw,
  Download,
  ShieldCheck,
  ChevronDown,
  ChevronRight,
  Mail,
  Calendar,
  FileText,
  Inbox,
} from "lucide-react";

const TABS = [
  { id: "contact", label: "Contact Submissions", path: "/contact-messages", csv: { path: "/contact-messages.csv", filename: "phoenix-contact-messages.csv" } },
  { id: "rental", label: "Rental Inquiries", path: "/rental-inquiries", csv: { path: "/rental-inquiries.csv", filename: "phoenix-rental-inquiries.csv" } },
  { id: "newsletter", label: "Newsletter Signups", path: "/newsletter-signups", csv: { path: "/newsletter-signups.csv", filename: "phoenix-newsletter-signups.csv" } },
];

function NoIndex() {
  return (
    <Helmet>
      <title>Phoenix Leadership — Inquiries Dashboard</title>
      <meta name="robots" content="noindex,nofollow,noarchive,nosnippet" />
      <meta name="googlebot" content="noindex,nofollow" />
    </Helmet>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return iso;
  }
}

function LoginScreen({ onLoggedIn }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await adminLogin(password);
      onLoggedIn();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-12 bg-[#0A0A0A]" data-testid="leadership-login">
      <NoIndex />
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 mb-4">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <div className="overline">Phoenix · Executive Board Only</div>
          <h1 className="font-display text-3xl text-white mt-2">Leadership Dashboard</h1>
          <p className="text-slate-400 text-sm mt-3 leading-relaxed">
            Read-only view of contact submissions, rental inquiries, and
            newsletter signups. Sessions expire after 12 hours.
          </p>
        </div>
        <form onSubmit={submit} className="card-dark p-7 space-y-4" data-testid="leadership-login-form">
          <div>
            <label className="block overline mb-2" htmlFor="leadership-password">Shared Password</label>
            <input
              id="leadership-password"
              type="password"
              autoFocus
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-500/60 transition-colors"
              placeholder="Enter shared password"
              required
              data-testid="leadership-password-input"
            />
          </div>
          {error && (
            <div className="text-red-400 text-sm" data-testid="leadership-login-error">{error}</div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="btn-gold w-full justify-center disabled:opacity-60"
            data-testid="leadership-login-submit"
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
          <p className="text-xs text-slate-500 text-center leading-relaxed">
            This area is not linked from the public site and is excluded from search engines. Contact the website administrator to rotate the password.
          </p>
        </form>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="card-dark p-5 flex items-center gap-4">
      <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 flex-shrink-0">
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <div className="overline">{label}</div>
        <div className="text-white font-display text-2xl mt-1">{value}</div>
      </div>
    </div>
  );
}

function ContactRow({ item, expanded, onToggle }) {
  return (
    <>
      <tr className="border-t border-white/5 hover:bg-white/[0.02] cursor-pointer" onClick={onToggle} data-testid={`row-contact-${item.id}`}>
        <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">{fmtDate(item.created_at)}</td>
        <td className="px-4 py-3 text-white text-sm">{item.name}</td>
        <td className="px-4 py-3 text-amber-400 text-sm truncate max-w-[220px]">{item.email}</td>
        <td className="px-4 py-3 text-slate-300 text-sm">{item.inquiry_type || "—"}</td>
        <td className="px-4 py-3 text-slate-200 text-sm truncate max-w-[280px]">{item.subject}</td>
        <td className="px-4 py-3 text-slate-500">{expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</td>
      </tr>
      {expanded && (
        <tr className="bg-white/[0.02]">
          <td colSpan={6} className="px-4 py-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div><div className="overline mb-1">Phone</div><div className="text-slate-200">{item.phone || "—"}</div></div>
              <div><div className="overline mb-1">Organization</div><div className="text-slate-200">{item.organization || "—"}</div></div>
              {item.interest_areas?.length > 0 && (
                <div className="md:col-span-2"><div className="overline mb-1">Interest Areas</div><div className="text-slate-200">{item.interest_areas.join(", ")}</div></div>
              )}
              <div className="md:col-span-2"><div className="overline mb-1">Message</div><div className="text-slate-200 whitespace-pre-wrap leading-relaxed">{item.message}</div></div>
              {item.extra_fields && Object.keys(item.extra_fields).length > 0 && (
                <div className="md:col-span-2">
                  <div className="overline mb-1">Extra Fields</div>
                  <pre className="text-xs text-slate-300 bg-black/30 rounded-lg p-3 overflow-x-auto">{JSON.stringify(item.extra_fields, null, 2)}</pre>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function RentalRow({ item, expanded, onToggle }) {
  return (
    <>
      <tr className="border-t border-white/5 hover:bg-white/[0.02] cursor-pointer" onClick={onToggle} data-testid={`row-rental-${item.id}`}>
        <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">{fmtDate(item.created_at)}</td>
        <td className="px-4 py-3 text-white text-sm">{item.name}</td>
        <td className="px-4 py-3 text-amber-400 text-sm truncate max-w-[220px]">{item.email}</td>
        <td className="px-4 py-3 text-slate-300 text-sm">{item.event_type || "—"}</td>
        <td className="px-4 py-3 text-slate-300 text-sm whitespace-nowrap">{item.event_date || "—"}</td>
        <td className="px-4 py-3 text-slate-300 text-sm">{item.guest_count ?? "—"}</td>
        <td className="px-4 py-3 text-slate-500">{expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</td>
      </tr>
      {expanded && (
        <tr className="bg-white/[0.02]">
          <td colSpan={7} className="px-4 py-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div><div className="overline mb-1">Phone</div><div className="text-slate-200">{item.phone || "—"}</div></div>
              <div><div className="overline mb-1">Organization</div><div className="text-slate-200">{item.organization || "—"}</div></div>
              <div><div className="overline mb-1">Rental Type</div><div className="text-slate-200">{item.rental_type || "—"}</div></div>
              <div><div className="overline mb-1">Duration</div><div className="text-slate-200">{item.estimated_duration || "—"}</div></div>
              <div><div className="overline mb-1">Date Flexibility</div><div className="text-slate-200">{item.flexibility || "—"}</div></div>
              <div><div className="overline mb-1">Referral Source</div><div className="text-slate-200">{item.referral_source || "—"}</div></div>
              <div><div className="overline mb-1">Walkthrough Requested</div><div className="text-slate-200">{item.walkthrough_requested ? "Yes" : "No"}</div></div>
              <div className="md:col-span-3"><div className="overline mb-1">Notes</div><div className="text-slate-200 whitespace-pre-wrap leading-relaxed">{item.notes || "—"}</div></div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function NewsletterRow({ item }) {
  return (
    <tr className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`row-newsletter-${item.id}`}>
      <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">{fmtDate(item.created_at)}</td>
      <td className="px-4 py-3 text-amber-400 text-sm break-all">{item.email}</td>
    </tr>
  );
}

function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("contact");
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ contact_messages: 0, rental_inquiries: 0, newsletter_signups: 0 });
  const [loading, setLoading] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [lastRefresh, setLastRefresh] = useState(null);
  const [error, setError] = useState("");

  const activeTab = useMemo(() => TABS.find((t) => t.id === tab), [tab]);

  const loadSummary = async () => {
    try {
      const s = await adminFetch("/summary");
      setSummary(s);
    } catch (err) {
      if (err?.response?.status === 401) {
        adminAuth.clear();
        onLogout();
        return;
      }
    }
  };

  const loadTab = async () => {
    setLoading(true);
    setError("");
    setExpanded({});
    try {
      const data = await adminFetch(activeTab.path);
      setItems(data.items || []);
      setLastRefresh(new Date());
    } catch (err) {
      if (err?.response?.status === 401) {
        adminAuth.clear();
        onLogout();
        return;
      }
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSummary(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadTab(); /* eslint-disable-next-line */ }, [tab]);

  const handleCsv = async () => {
    setCsvLoading(true);
    try {
      const ts = new Date().toISOString().slice(0, 10);
      const fn = activeTab.csv.filename.replace(".csv", `-${ts}.csv`);
      await adminDownloadCsv(activeTab.csv.path, fn);
      toast.success("CSV downloaded.");
    } catch (err) {
      if (err.message?.includes("Session expired")) {
        onLogout();
        return;
      }
      toast.error(err.message || "CSV download failed.");
    } finally {
      setCsvLoading(false);
    }
  };

  const handleLogout = () => {
    adminAuth.clear();
    onLogout();
  };

  const toggle = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="leadership-dashboard">
      <NoIndex />

      {/* Header */}
      <header className="border-b border-white/10 bg-black/60 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 flex-shrink-0">
              <ShieldCheck className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <div className="overline truncate">Phoenix · Executive Board</div>
              <div className="font-display text-lg text-white leading-tight truncate">Inquiries Dashboard</div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={loadTab} className="btn-ghost text-sm" disabled={loading} data-testid="leadership-refresh">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button onClick={handleLogout} className="btn-ghost text-sm" data-testid="leadership-logout">
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Log Out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 lg:px-8 py-8 space-y-8">
        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="leadership-summary">
          <StatCard icon={Mail} label="Contact Submissions" value={summary.contact_messages} />
          <StatCard icon={Calendar} label="Rental Inquiries" value={summary.rental_inquiries} />
          <StatCard icon={Inbox} label="Newsletter Signups" value={summary.newsletter_signups} />
        </div>

        {/* Tabs */}
        <div className="card-dark p-0 overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b border-white/10">
            <div className="flex flex-wrap gap-2" data-testid="leadership-tabs">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`text-sm px-4 py-2 rounded-full border transition-colors ${tab === t.id ? "border-amber-500/60 bg-amber-500/[0.1] text-amber-300" : "border-white/10 text-slate-300 hover:border-white/20"}`}
                  data-testid={`leadership-tab-${t.id}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-500">
              {lastRefresh && <span>Refreshed {lastRefresh.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}</span>}
              <button
                onClick={handleCsv}
                disabled={csvLoading || items.length === 0}
                className="inline-flex items-center gap-2 text-sm text-amber-400 hover:text-amber-300 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="leadership-csv"
              >
                <Download className="h-4 w-4" />
                {csvLoading ? "Preparing…" : "Download CSV"}
              </button>
            </div>
          </div>

          {/* Table area */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="px-6 py-16 text-center text-slate-400 text-sm" data-testid="leadership-loading">Loading…</div>
            ) : error ? (
              <div className="px-6 py-16 text-center text-red-400 text-sm" data-testid="leadership-error">{error}</div>
            ) : items.length === 0 ? (
              <div className="px-6 py-16 text-center text-slate-500 text-sm" data-testid="leadership-empty">
                No {activeTab.label.toLowerCase()} yet.
              </div>
            ) : tab === "contact" ? (
              <table className="w-full text-left">
                <thead>
                  <tr className="text-xs uppercase tracking-[0.18em] text-slate-500">
                    <th className="px-4 py-3 font-normal">Received</th>
                    <th className="px-4 py-3 font-normal">Name</th>
                    <th className="px-4 py-3 font-normal">Email</th>
                    <th className="px-4 py-3 font-normal">Type</th>
                    <th className="px-4 py-3 font-normal">Subject</th>
                    <th className="px-4 py-3" aria-label="Expand" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <ContactRow key={it.id} item={it} expanded={!!expanded[it.id]} onToggle={() => toggle(it.id)} />
                  ))}
                </tbody>
              </table>
            ) : tab === "rental" ? (
              <table className="w-full text-left">
                <thead>
                  <tr className="text-xs uppercase tracking-[0.18em] text-slate-500">
                    <th className="px-4 py-3 font-normal">Received</th>
                    <th className="px-4 py-3 font-normal">Name</th>
                    <th className="px-4 py-3 font-normal">Email</th>
                    <th className="px-4 py-3 font-normal">Event Type</th>
                    <th className="px-4 py-3 font-normal">Event Date</th>
                    <th className="px-4 py-3 font-normal">Guests</th>
                    <th className="px-4 py-3" aria-label="Expand" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <RentalRow key={it.id} item={it} expanded={!!expanded[it.id]} onToggle={() => toggle(it.id)} />
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="w-full text-left">
                <thead>
                  <tr className="text-xs uppercase tracking-[0.18em] text-slate-500">
                    <th className="px-4 py-3 font-normal">Subscribed</th>
                    <th className="px-4 py-3 font-normal">Email</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <NewsletterRow key={it.id} item={it} />
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="px-4 sm:px-6 py-3 border-t border-white/10 text-xs text-slate-500 flex items-center gap-2">
            <FileText className="h-3.5 w-3.5" />
            Showing {items.length} {items.length === 1 ? "record" : "records"} · newest first · read-only
          </div>
        </div>

        <div className="text-xs text-slate-500 text-center pt-4">
          Phoenix of the Detroit Fire Department · Internal leadership view · This page is excluded from search engines.
        </div>
      </main>
    </div>
  );
}

export default function Leadership() {
  const [authed, setAuthed] = useState(() => !!adminAuth.getToken());

  return authed ? (
    <Dashboard onLogout={() => setAuthed(false)} />
  ) : (
    <LoginScreen onLoggedIn={() => setAuthed(true)} />
  );
}
